import html2pdf from "html2pdf.js";
import { fileDownloadApi } from "../Api/common";
import { extractDate, getImgType } from "../Constant/common";
import { BaseStaffURL } from "../Api/Service";
import logoLight from "../assets/images/logo/white-based-logo-long.svg";
import { logError } from "./logger";

export const DEFAULT_OFFER_TEMPLATE = `<p>Date: {letter_date}</p>
<p>Dear {name},</p>
<p>We are pleased to offer you the position of <strong>{designation}</strong> with <strong>{organization_name}</strong>.</p>
<p>Your annual CTC will be INR <strong>{yearly_salary}</strong> (Monthly Gross: INR <strong>{monthly_salary}</strong>) and your tentative date of joining will be <strong>{joining_date}</strong>. You will be reporting to our <strong>{center_name}</strong> location.</p>
<p>Please return a signed copy of this letter as acceptance of our offer.</p>
<p>Sincerely,<br>Authorized Signatory<br><strong>{organization_name}</strong></p>`;

export const DEFAULT_JOINING_TEMPLATE = `<p>Date: {letter_date}</p>
<p>Dear {name},</p>
<p>We are pleased to offer you employment with <strong>{organization_name}</strong> as a <strong>{designation}</strong>.</p>
<p>Your date of joining will be <strong>{joining_date}</strong>. You will be based at our <strong>{center_name}</strong> location. Your monthly gross salary will be INR <strong>{monthly_salary}</strong>.</p>
<p>We look forward to a mutually beneficial association.</p>
<p>Sincerely,<br>Authorized Signatory<br><strong>{organization_name}</strong></p>`;

export const DEFAULT_TRAINING_TEMPLATE = `<p>Date: {letter_date}</p>
<p>Dear {name},</p>
<p>We are pleased to inform you that you have been selected for the Training Program at <strong>{organization_name}</strong> as a <strong>{designation}</strong> trainee.</p>
<p>Your training will commence on <strong>{joining_date}</strong> at our <strong>{center_name}</strong> office for a period of six months. During this period, you will receive a monthly stipend of INR <strong>{monthly_salary}</strong>.</p>
<p>Please note that this training program does not guarantee permanent employment.</p>
<p>Sincerely,<br>Authorized Signatory<br><strong>{organization_name}</strong></p>`;

export const DEFAULT_EXPERIENCE_TEMPLATE = `<p>Date: {letter_date}</p>
<p style="text-align: center;"><strong>TO WHOMSOEVER IT MAY CONCERN</strong></p>
<p>This is to certify that <strong>{name}</strong> was employed with <strong>{organization_name}</strong> as a <strong>{designation}</strong> from <strong>{joining_date}</strong> to <strong>{relieving_date}</strong>.</p>
<p>During their tenure, we found them to be diligent, hardworking, and honest. We wish them all the success in their future endeavors.</p>
<p>Sincerely,<br>Authorized Signatory<br><strong>{organization_name}</strong></p>`;

export const DEFAULT_RELIEVING_TEMPLATE = `<p>Date: {letter_date}</p>
<p>Dear {name},</p>
<p>This has reference to your resignation from the services of <strong>{organization_name}</strong>.</p>
<p>We wish to inform you that you have been officially relieved from your duties as a <strong>{designation}</strong> with effect from the close of business hours on <strong>{relieving_date}</strong>.</p>
<p>We appreciate your contributions during your tenure and wish you the best in your future career.</p>
<p>Sincerely,<br>Authorized Signatory<br><strong>{organization_name}</strong></p>`;

export const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("data:") || path.startsWith("http")) return path;

  const baseUrl = BaseStaffURL || "";
  let relativePath = path;
  if (baseUrl.endsWith("staff/") && relativePath.startsWith("staff/")) {
    relativePath = relativePath.substring("staff/".length);
  }
  return `${baseUrl}${relativePath}`;
};

export const getFolderAndFilename = (path) => {
  if (!path) return "";
  if (path.startsWith("data:")) return "";

  if (path.startsWith("http")) {
    const staffIndex = path.indexOf("staff/");
    if (staffIndex !== -1) {
      return path.substring(staffIndex);
    }
    const lastSlash = path.lastIndexOf("/");
    return `staff/${path.substring(lastSlash + 1)}`;
  }

  if (path.includes("staff/")) {
    return path;
  }
  return `staff/${path}`;
};

export const getBase64FromApi = async (path, fallbackUrl) => {
  if (!path) return "";
  if (path.startsWith("data:")) return path;

  const fileName = getFolderAndFilename(path);
  if (!fileName) return fallbackUrl || "";

  try {
    const data = await fileDownloadApi({ fileName });
    if (!data) return fallbackUrl || "";

    if (data instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder("utf-8").decode(new Uint8Array(data.slice(0, 100)));
        if (text.trim().startsWith("{")) {
          logError("API returned JSON error instead of file binary:", text);
          return fallbackUrl || "";
        }
      } catch (e) {
        // Ignore decoding errors
      }
    }

    const contentType = getImgType(fileName);
    const blob = new Blob([data], { type: contentType });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    logError("Failed to download file from API", fileName, err);
    return fallbackUrl || "";
  }
};

export const convertSvgToPngBase64 = (svgSrc, width = 300) => {
  return new Promise((resolve) => {
    const img = new Image();
    if (svgSrc && !svgSrc.startsWith("data:") && !svgSrc.startsWith(window.location.origin)) {
      img.crossOrigin = "anonymous";
    }
    img.src = svgSrc;
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const aspect = (img.naturalHeight || img.height) / (img.naturalWidth || img.width) || 1;
        const renderWidth = width * 2;
        const renderHeight = renderWidth * aspect;
        canvas.width = renderWidth;
        canvas.height = renderHeight;
        ctx.clearRect(0, 0, renderWidth, renderHeight);
        ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
        const pngBase64 = canvas.toDataURL("image/png");
        resolve(pngBase64);
      } catch (err) {
        logError("Failed to convert SVG to PNG", err);
        resolve(svgSrc);
      }
    };
    img.onerror = (err) => {
      logError("Error loading SVG for PNG conversion", err);
      resolve(svgSrc);
    };
  });
};

export const resolvePlaceholders = (template, staff, values = {}, salaryData = null) => {
  if (!template) return "";

  const orgName = staff?.organization?.organization_name || "our organization";
  const centerName = staff?.primaryCenter?.center_name || "office";
  const joiningDate = staff?.staffGeneralInformation?.date_of_joining
    ? extractDate(staff.staffGeneralInformation.date_of_joining)
    : staff?.date_of_joining
      ? extractDate(staff.date_of_joining)
      : extractDate(new Date());

  let salary = "-";
  if (salaryData?.monthly_salary !== undefined && salaryData?.monthly_salary !== null) {
    salary = Number(salaryData.monthly_salary).toLocaleString("en-IN");
  } else if (staff?.staffGeneralInformation?.monthly_salary) {
    salary = Number(staff.staffGeneralInformation.monthly_salary).toLocaleString("en-IN");
  } else if (staff?.monthly_salary) {
    salary = Number(staff.monthly_salary).toLocaleString("en-IN");
  }

  let yearlySalary = "-";
  if (salaryData?.yearly_salary !== undefined && salaryData?.yearly_salary !== null) {
    yearlySalary = Number(salaryData.yearly_salary).toLocaleString("en-IN");
  } else if (staff?.staffGeneralInformation?.yearly_salary) {
    yearlySalary = Number(staff.staffGeneralInformation.yearly_salary).toLocaleString("en-IN");
  } else if (staff?.yearly_salary) {
    yearlySalary = Number(staff.yearly_salary).toLocaleString("en-IN");
  }

  const name = staff?.name || "Employee";
  const designation = values?.designation || staff?.position?.position_name || "Staff";
  const relievingDate = values?.relieving_date
    ? extractDate(values.relieving_date)
    : extractDate(new Date());
  const letterDate = values?.letter_date
    ? extractDate(values.letter_date)
    : extractDate(new Date());

  const replacements = {
    name,
    designation,
    joining_date: joiningDate,
    monthly_salary: salary,
    yearly_salary: yearlySalary,
    relieving_date: relievingDate,
    letter_date: letterDate,
    organization_name: orgName,
    center_name: centerName,
  };

  let resolved = template;
  Object.keys(replacements).forEach((key) => {
    resolved = resolved.replaceAll(`{${key}}`, replacements[key] ?? "");
  });
  return resolved;
};

export const htmlToText = (html) => {
  if (!html) return "";
  let text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<[^>]+>/g, "");

  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  return text.replace(/\n{3,}/g, "\n\n").trim();
};

export const formatBodyToHtml = (body) => {
  if (!body) return "";
  const trimmed = body.trim();
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return trimmed;
  }
  return trimmed
    .split(/\n\s*\n/)
    .map((paragraph) => `<p>${paragraph.trim().replace(/\n/g, "<br/>")}</p>`)
    .join("\n");
};

export const getDefaultLetterBody = (
  type,
  staff,
  rDate,
  lDate,
  des,
  customTemplates = null,
  salaryData = null,
) => {
  let template = "";
  if (customTemplates && customTemplates[type]) {
    template = customTemplates[type];
  } else {
    const saved = localStorage.getItem(`${type}_letter_template`);
    if (saved) {
      template = saved;
    } else {
      switch (type) {
        case "offer":
          template = DEFAULT_OFFER_TEMPLATE;
          break;
        case "joining":
          template = DEFAULT_JOINING_TEMPLATE;
          break;
        case "training":
          template = DEFAULT_TRAINING_TEMPLATE;
          break;
        case "experience":
          template = DEFAULT_EXPERIENCE_TEMPLATE;
          break;
        case "relieving":
          template = DEFAULT_RELIEVING_TEMPLATE;
          break;
        default:
          template = DEFAULT_JOINING_TEMPLATE;
      }
    }
  }

  const resolved = resolvePlaceholders(
    template,
    staff,
    {
      designation: des,
      letter_date: lDate,
      relieving_date: rDate,
    },
    salaryData,
  );

  return htmlToText(resolved);
};

export const printLetter = (title, staff, bodyHtml, settings = {}) => {
  const {
    includeLogo = false,
    logoPath = "",
    logoWidth = "120",
    logoAlign = "left",
    includeSignature = false,
    signatoryName = "Authorized Signatory",
    signaturePath = "",
    signatoryTitle = "Authorized Signatory",
    logoBase64 = "",
    signatureBase64 = "",
  } = settings;

  const orgName = staff?.organization?.organization_name || "our organization";
  const centerName = staff?.primaryCenter?.center_name || "office";

  let logoHtml = "";
  if (includeLogo) {
    const src = logoBase64
      ? logoBase64
      : logoPath && logoPath !== "logo.png" && logoPath !== "/logo.png"
        ? getImageUrl(logoPath)
        : logoLight;
    logoHtml = `<div class="logo-container"><img src="${src}" style="width: ${logoWidth}px; height: auto; display: block;" alt="Logo" /></div>`;
  }

  let signatureSectionHtml = "";
  if (includeSignature) {
    let signatureImgOrText = "";
    if (signatureBase64) {
      signatureImgOrText = `<img src="${signatureBase64}" style="max-height: 45px; display: block; margin-left: auto; margin-bottom: 4px;" alt="Signature" />`;
    } else if (signaturePath) {
      signatureImgOrText = `<img src="${getImageUrl(signaturePath)}" style="max-height: 45px; display: block; margin-left: auto; margin-bottom: 4px;" alt="Signature" />`;
    } else {
      signatureImgOrText = `<div class="stylized-signature">${signatoryName}</div>`;
    }

    signatureSectionHtml = `
      <div class="stamp-container">
        <div class="stamp-badge">Approved</div>
        ${signatureImgOrText}
      </div>
    `;
  } else {
    signatureSectionHtml = `<div style="height: 45px;"></div>`;
  }

  const formattedBody = formatBodyToHtml(bodyHtml);

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title.replaceAll("_", " ")} - ${staff?.name || "Staff"}</title>
  <style>
    @page { 
      size: A4; 
      margin: 20mm; 
    }
    body { 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; 
      font-size: 14px; 
      color: #333; 
      margin: 0; 
      line-height: 1.6;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .letterhead {
      border-bottom: 2px solid #333;
      padding-bottom: 12px;
      margin-bottom: 24px;
      display: flex;
      flex-direction: ${includeLogo ? (logoAlign === "center" ? "column" : logoAlign === "left" ? "row" : "row-reverse") : "row"};
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    }
    .logo-container {
      flex-shrink: 0;
    }
    .org-details {
      text-align: ${includeLogo ? (logoAlign === "center" ? "center" : logoAlign === "left" ? "right" : "left") : "left"};
    }
    .org-name {
      margin: 0;
      font-weight: 800;
      color: #111;
      font-size: 19px;
      letter-spacing: -0.02em;
      text-transform: uppercase;
    }
    .center-name {
      margin: 4px 0 0 0;
      color: #666;
      font-size: 12px;
      font-weight: 500;
    }
    .official-badge {
      padding: 4px 10px;
      border: 2px solid #333;
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .content-area {
      min-height: 250px;
      color: #222;
      margin-bottom: 30px;
    }
    .content-area p {
      margin-top: 0;
      margin-bottom: 1em;
    }

    .signature-row {
      margin-top: 40px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .prepared-by {
      text-align: left;
    }
    .prepared-label {
      margin: 0;
      font-size: 12px;
      color: #666;
    }
    .prepared-value {
      margin: 4px 0 0 0;
      font-weight: 600;
      color: #222;
      font-size: 13px;
    }
    
    .signatory-details {
      text-align: right;
      position: relative;
    }
    .stamp-container {
      position: relative;
      margin-bottom: 4px;
    }
    .stamp-badge {
      position: absolute;
      right: 50px;
      bottom: 10px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 2px dashed rgba(99, 102, 241, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      color: rgba(99, 102, 241, 0.6);
      font-weight: 800;
      text-transform: uppercase;
      transform: rotate(-15deg);
    }
    .stylized-signature {
      font-family: 'Courier New', Courier, monospace;
      font-style: italic;
      font-size: 16px;
      font-weight: 700;
      color: #1e3a8a;
      transform: rotate(-5deg);
      margin-bottom: 4px;
      padding-right: 10px;
      text-align: right;
    }
    .for-label {
      margin: 0;
      font-size: 12px;
      color: #666;
    }
    .signatory-title {
      margin: 4px 0 0 0;
      font-weight: 700;
      color: #222;
      font-size: 13px;
    }
    .signatory-name-bracket {
      font-size: 11px;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="letterhead">
    ${logoHtml}
    <div class="org-details">
      <div class="org-name">${orgName}</div>
      <div class="center-name">${centerName}</div>
    </div>
    ${!includeLogo ? `<div class="official-badge">Official Document</div>` : ""}
  </div>
  
  <div class="content-area">
    ${formattedBody}
  </div>

  <div class="signature-row">
    <div class="prepared-by">
      <p class="prepared-label">Prepared By</p>
      <p class="prepared-value">HR Fusions</p>
    </div>
    <div class="signatory-details">
      ${signatureSectionHtml}
      <p class="for-label">For ${orgName}</p>
      <p class="signatory-title">${signatoryTitle}</p>
      ${includeSignature ? `<span class="signatory-name-bracket">(${signatoryName})</span>` : ""}
    </div>
  </div>
</body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow.focus();
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 500);
};

export const downloadLetterPdf = async (title, staff, bodyHtml, settings = {}) => {
  const {
    includeLogo = false,
    logoPath = "",
    logoWidth = "120",
    logoAlign = "left",
    includeSignature = false,
    signatoryName = "Authorized Signatory",
    signaturePath = "",
    signatoryTitle = "Authorized Signatory",
    logoBase64 = "",
    signatureBase64 = "",
  } = settings;

  const orgName = staff?.organization?.organization_name || "our organization";
  const centerName = staff?.primaryCenter?.center_name || "office";

  let finalLogoBase64 = "";
  if (includeLogo) {
    if (logoBase64) {
      finalLogoBase64 = logoBase64;
    } else if (logoPath && logoPath !== "logo.png" && logoPath !== "/logo.png") {
      const s3Url = getImageUrl(logoPath);
      finalLogoBase64 = await getBase64FromApi(logoPath, s3Url);
    } else {
      finalLogoBase64 = logoLight;
    }
  }

  let finalSignatureBase64 = "";
  if (includeSignature) {
    if (signatureBase64) {
      finalSignatureBase64 = signatureBase64;
    } else if (signaturePath) {
      const s3Url = getImageUrl(signaturePath);
      finalSignatureBase64 = await getBase64FromApi(signaturePath, s3Url);
    }
  }

  if (includeLogo && finalLogoBase64) {
    const isSvg =
      finalLogoBase64.startsWith("data:image/svg+xml") ||
      (logoPath && logoPath.toLowerCase().endsWith(".svg")) ||
      finalLogoBase64.toLowerCase().includes(".svg");
    if (isSvg) {
      const targetWidth = parseInt(logoWidth, 10) || 120;
      finalLogoBase64 = await convertSvgToPngBase64(finalLogoBase64, targetWidth);
    }
  }

  if (includeSignature && finalSignatureBase64) {
    const isSvg =
      finalSignatureBase64.startsWith("data:image/svg+xml") ||
      (signaturePath && signaturePath.toLowerCase().endsWith(".svg")) ||
      finalSignatureBase64.toLowerCase().includes(".svg");
    if (isSvg) {
      finalSignatureBase64 = await convertSvgToPngBase64(finalSignatureBase64, 150);
    }
  }

  let logoHtml = "";
  if (includeLogo && finalLogoBase64) {
    logoHtml = `<div class="logo-container"><img src="${finalLogoBase64}" crossorigin="anonymous" style="width: ${logoWidth}px; height: auto; display: block;" alt="Logo" /></div>`;
  }

  let signatureSectionHtml = "";
  if (includeSignature) {
    let signatureImgOrText = "";
    if (finalSignatureBase64) {
      signatureImgOrText = `<img src="${finalSignatureBase64}" crossorigin="anonymous" style="max-height: 45px; display: block; margin-left: auto; margin-bottom: 4px;" alt="Signature" />`;
    } else {
      signatureImgOrText = `<div class="stylized-signature">${signatoryName}</div>`;
    }

    signatureSectionHtml = `
      <div class="stamp-container">
        <div class="stamp-badge">Approved</div>
        ${signatureImgOrText}
      </div>
    `;
  } else {
    signatureSectionHtml = `<div style="height: 45px;"></div>`;
  }

  const formattedBody = formatBodyToHtml(bodyHtml);

  const htmlContent = `
    <div style="width: 720px; background: #ffffff; padding: 40px; color: #333333; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 14px; line-height: 1.6; box-sizing: border-box; text-align: left;">
      <style>
        .letterhead {
          border-bottom: 2px solid #333333;
          padding-bottom: 12px;
          margin-bottom: 24px;
          display: flex;
          flex-direction: ${includeLogo ? (logoAlign === "center" ? "column" : logoAlign === "left" ? "row" : "row-reverse") : "row"};
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }
        .logo-container {
          flex-shrink: 0;
        }
        .org-details {
          text-align: ${includeLogo ? (logoAlign === "center" ? "center" : logoAlign === "left" ? "right" : "left") : "left"};
        }
        .org-name {
          margin: 0;
          font-weight: 800;
          color: #111111;
          font-size: 19px;
          letter-spacing: -0.02em;
          text-transform: uppercase;
        }
        .center-name {
          margin: 4px 0 0 0;
          color: #666666;
          font-size: 12px;
          font-weight: 500;
        }
        .official-badge {
          padding: 4px 10px;
          border: 2px solid #333333;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .content-area {
          min-height: 350px;
          color: #222222;
          margin-bottom: 30px;
        }
        .content-area p {
          margin-top: 0;
          margin-bottom: 1em;
        }

        .signature-row {
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .prepared-by {
          text-align: left;
        }
        .prepared-label {
          margin: 0;
          font-size: 12px;
          color: #666666;
        }
        .prepared-value {
          margin: 4px 0 0 0;
          font-weight: 600;
          color: #222222;
          font-size: 13px;
        }
        
        .signatory-details {
          text-align: right;
          position: relative;
        }
        .stamp-container {
          position: relative;
          margin-bottom: 4px;
        }
        .stamp-badge {
          position: absolute;
          right: 50px;
          bottom: 10px;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          border: 2px dashed rgba(99, 102, 241, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          color: rgba(99, 102, 241, 0.6);
          font-weight: 800;
          text-transform: uppercase;
          transform: rotate(-15deg);
          pointer-events: none;
        }
        .stylized-signature {
          font-family: 'Courier New', Courier, monospace;
          font-style: italic;
          font-size: 16px;
          font-weight: 700;
          color: #1e3a8a;
          transform: rotate(-5deg);
          margin-bottom: 4px;
          padding-right: 10px;
          text-align: right;
        }
        .for-label {
          margin: 0;
          font-size: 12px;
          color: #666666;
        }
        .signatory-title {
          margin: 4px 0 0 0;
          font-weight: 700;
          color: #222222;
          font-size: 13px;
        }
        .signatory-name-bracket {
          font-size: 11px;
          color: #666666;
        }
      </style>

      <div class="letterhead">
        ${logoHtml}
        <div class="org-details">
          <div class="org-name">${orgName}</div>
          <div class="center-name">${centerName}</div>
        </div>
        ${!includeLogo ? `<div class="official-badge">Official Document</div>` : ""}
      </div>
      
      <div class="content-area">
        ${formattedBody}
      </div>

      <div class="signature-row">
        <div class="prepared-by">
          <p class="prepared-label">Prepared By</p>
          <p class="prepared-value">HR Fusions</p>
        </div>
        <div class="signatory-details">
          ${signatureSectionHtml}
          <p class="for-label">For ${orgName}</p>
          <p class="signatory-title">${signatoryTitle}</p>
          ${includeSignature ? `<span class="signatory-name-bracket">(${signatoryName})</span>` : ""}
        </div>
      </div>
    </div>
  `;

  const cleanTitle = title ? title.replace(/\s+/g, "_") : "Document";
  const filename = `${staff?.name || "Staff"}_${cleanTitle}.pdf`;
  const options = {
    margin: [15, 15, 15, 15],
    filename: filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };

  const imageSources = [];
  if (includeLogo && finalLogoBase64) {
    imageSources.push(finalLogoBase64);
  }
  if (includeSignature && finalSignatureBase64) {
    imageSources.push(finalSignatureBase64);
  }

  const preloadPromises = imageSources.map((src) => {
    return new Promise((resolve) => {
      const img = new Image();
      if (src && !src.startsWith("data:") && !src.startsWith(window.location.origin)) {
        img.crossOrigin = "anonymous";
      }
      img.src = src;
      if (img.complete) {
        resolve();
      } else {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      }
    });
  });

  try {
    await Promise.all(preloadPromises);
    await new Promise((resolve) => setTimeout(resolve, 150));
    await html2pdf().from(htmlContent).set(options).save();
    return true;
  } catch (err) {
    logError("PDF download failed", err);
    throw err;
  }
};
